<script setup lang="ts">
import { ArrowRight } from 'lucide-vue-next'
import ConnectionsLayout from '@/modules/shared/layouts/connections-layout.vue'
import OrbitalAnimation from '../components/groups/orbital-animation.vue'

const LIQUIFI_URL = 'https://www.liquifi.com'

const CARDS = [
  {
    icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16v-4M12 8h.01',
    title: "What's changing",
    description: 'Connections matchmaking is winding down and moving to LIQUIFI, our Broker/Dealer partner.',
  },
  {
    icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3',
    title: 'What you need to do',
    description: 'Nothing — no action is required from you.',
  },
  {
    icon: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3',
    title: 'Learn more',
    description: 'Visit liquifi.com to find out more about LIQUIFI.',
    link: true,
  },
]
</script>

<template>
  <ConnectionsLayout>
    <div class="welcome-fullbleed">
      <div class="grid min-h-full grid-cols-1 pb-20 lg:grid-cols-2" style="background: #162a3a">
        <div class="z-10 flex flex-col justify-center bg-transparent px-6 py-6 sm:px-10 sm:py-8 lg:px-14 lg:py-10">
          <div class="label mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#3b88af]">
            Connections by Venture360
          </div>

          <h1
            class="title mb-4 mt-3 text-balance text-3xl font-bold leading-[1.1] tracking-wide text-white sm:text-4xl lg:text-[42px]"
          >
            Connections is moving to <span class="italic text-[#3b88af]">LIQUIFI</span>
          </h1>

          <p class="subtitle mb-8 max-w-[480px] text-base leading-relaxed text-gray-300 lg:text-lg">
            Connections will now be facilitated through our Broker/Dealer LIQUIFI. To learn more about LIQUIFI,
            <br />
            <a
              :href="LIQUIFI_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="font-semibold text-[#3b88af] underline underline-offset-4 hover:text-white"
              >click here</a
            >.
          </p>

          <div class="cta-wrap">
            <a
              :href="LIQUIFI_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 rounded-xl border border-[#2f7294] bg-[#3b88af] px-6 py-3 text-base font-medium text-white shadow-[0_1px_3px_rgba(0,0,0,0.12),0_2px_8px_rgba(59,136,175,0.25)] transition-all hover:shadow-[0_2px_6px_rgba(0,0,0,0.12),0_4px_16px_rgba(59,136,175,0.3)] active:shadow-sm"
            >
              Learn more about LIQUIFI
              <ArrowRight :size="16" :stroke-width="2" />
            </a>
          </div>
        </div>

        <div class="hidden lg:block">
          <div class="h-full">
            <OrbitalAnimation />
          </div>
        </div>
      </div>

      <div
        class="steps-section relative z-20 -mt-20 shrink-0 border-t border-white/[0.08] bg-[#162a3a] px-6 py-6 sm:px-10 sm:py-7 lg:px-14"
      >
        <div class="mb-5">
          <div class="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#3b88af]">The transition</div>
          <div class="text-xl font-bold text-white sm:text-2xl">What this means for you</div>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div
            v-for="card in CARDS"
            :key="card.title"
            class="step-card rounded-xl border border-white/[0.06] bg-white/[0.04] p-5"
          >
            <div class="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[#3b88af]/80">
              <svg
                fill="none"
                stroke="white"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                stroke-linecap="round"
                stroke-linejoin="round"
                width="20"
                height="20"
              >
                <path :d="card.icon" />
              </svg>
            </div>

            <div class="mb-1.5 text-[15px] font-bold text-white">{{ card.title }}</div>
            <div class="text-sm leading-relaxed text-gray-300">
              <template v-if="card.link">
                Visit
                <a
                  :href="LIQUIFI_URL"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-semibold text-[#3b88af] underline underline-offset-4 hover:text-white"
                  >liquifi.com</a
                >
                to find out more about LIQUIFI.
              </template>
              <template v-else>{{ card.description }}</template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ConnectionsLayout>
</template>

<style scoped>
.welcome-fullbleed {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  overflow-y: auto;
  overflow-x: hidden;
}

.label {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.6s both;
}

.title {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.7s both;
}

.subtitle {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.78s both;
}

.cta-wrap {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.86s both;
}

.steps-section {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 1s both;
}

.step-card:nth-child(1) {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 1.05s both;
}

.step-card:nth-child(2) {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 1.15s both;
}

.step-card:nth-child(3) {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 1.25s both;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .label,
  .title,
  .subtitle,
  .cta-wrap,
  .steps-section,
  .step-card {
    animation: none;
  }
}
</style>
